// @ts-nocheck
/**
 * Contact form validation
 * Follows project patterns from customerRegister.js
 */

import { debounce } from '@/js/utils';

(() => {
  'use strict';

  /* ================= main ================= */

  const ContactForm = {
    errClass: 'input--error',
    form: /** @type {HTMLFormElement | null} */ (null),
    email: /** @type {HTMLInputElement | null} */ (null),
    message: /** @type {HTMLTextAreaElement | null} */ (null),
    terms: /** @type {HTMLInputElement | null} */ (null),
    emailError: /** @type {HTMLElement | null} */ (null),
    messageError: /** @type {HTMLElement | null} */ (null),
    termsError: /** @type {HTMLElement | null} */ (null),
    submitBtn: /** @type {HTMLButtonElement | null} */ (null),
    errorBanner: /** @type {HTMLElement | null} */ (null),
    messages: /** @type {Record<string, string>} */ ({}),

    init() {
      this.form = document.querySelector('.contact__form');
      if (!this.form) return;

      this.email = document.querySelector('#contact-email');
      this.message = document.querySelector('#contact-message');
      this.terms = document.querySelector('#contact-terms');

      this.emailError = document.querySelector('#contact-email-error');
      this.messageError = document.querySelector('#contact-message-error');
      this.termsError = document.querySelector('#contact-terms-error');

      this.submitBtn = this.form.querySelector('button[type="submit"]');
      this.errorBanner = document.querySelector('.contact__error-banner');

      // Error messages
      const locale = document.documentElement.lang === 'uk' ? 'uk' : 'ru';
      this.messages = {
        required: locale === 'uk' ? 'Необхідно заповнити поле' : 'Необходимо заполнить поле',
        wrongEmail: locale === 'uk' ? 'Введіть коректний email у форматі example@domain.com' : 'Введите корректный email в формате example@domain.com',
        terms: locale === 'uk' ? 'Необхідно погодитися з умовами' : 'Необходимо согласиться с условиями',
        banner: locale === 'uk' ? 'Будь ласка, заповніть усі необхідні поля форми.' : 'Пожалуйста, заполните все необходимые поля формы.'
      };

      // Hide errors initially if empty
      if (this.emailError && !this.emailError.textContent.trim()) {
        this.emailError.style.display = 'none';
      }
      if (this.messageError && !this.messageError.textContent.trim()) {
        this.messageError.style.display = 'none';
      }
      if (this.termsError && !this.termsError.textContent.trim()) {
        this.termsError.style.display = 'none';
      }

      // Real-time validation on blur
      this.email?.addEventListener('blur', () => this._validateEmail());
      this.message?.addEventListener('blur', () => this._validateMessage());
      this.terms?.addEventListener('change', () => {
        if (this.terms.checked) {
          this._clearTermsError();
        }
      });

      // Clear errors on input
      this.email?.addEventListener('input', debounce(() => {
        if (this.email.classList.contains(this.errClass)) {
          this._clearEmailError();
        }
      }));

      this.message?.addEventListener('input', debounce(() => {
        if (this.message.classList.contains(this.errClass)) {
          this._clearMessageError();
        }
      }));

      // Form submission — use capture so we run before any other handler
      this.form.addEventListener('submit', (e) => this._onSubmit(e), true);

      // Show error banner if form has server-side errors
      if (this.email?.classList.contains(this.errClass) || 
          this.message?.classList.contains(this.errClass)) {
        this._showErrorBanner();
      }
    },

    /* ---------- validators ---------- */

    _validateEmail() {
      const re = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
      const value = (this.email?.value || '').trim();

      if (this.emailError) this.emailError.style.display = 'none';
      if (this.email) this.email.classList.remove(this.errClass);
      const inputGroup = this.email?.closest('.input, .textarea');
      if (inputGroup) inputGroup.classList.remove('input--error');

      if (!value) {
        this._setEmailError(this.messages.required);
        return false;
      }

      if (!re.test(value)) {
        this._setEmailError(this.messages.wrongEmail);
        return false;
      }

      return true;
    },

    _setEmailError(msg) {
      if (this.emailError) {
        this.emailError.textContent = msg ?? '';
        this.emailError.style.display = '';
      }
      if (this.email) this.email.classList.add(this.errClass);
      const inputGroup = this.email?.closest('.input, .textarea');
      if (inputGroup) inputGroup.classList.add('input--error');
    },

    _clearEmailError() {
      if (this.emailError) this.emailError.style.display = 'none';
      if (this.email) this.email.classList.remove(this.errClass);
      const inputGroup = this.email?.closest('.input, .textarea');
      if (inputGroup) inputGroup.classList.remove('input--error');
      this._hideErrorBanner();
    },

    _validateMessage() {
      const value = (this.message?.value || '').trim();

      if (this.messageError) this.messageError.style.display = 'none';
      if (this.message) this.message.classList.remove(this.errClass);
      const inputGroup = this.message?.closest('.input, .textarea');
      if (inputGroup) inputGroup.classList.remove('input--error');

      if (!value) {
        this._setMessageError(this.messages.required);
        return false;
      }

      return true;
    },

    _setMessageError(msg) {
      if (this.messageError) {
        this.messageError.textContent = msg ?? '';
        this.messageError.style.display = '';
      }
      if (this.message) this.message.classList.add(this.errClass);
      const inputGroup = this.message?.closest('.input, .textarea');
      if (inputGroup) inputGroup.classList.add('input--error');
    },

    _clearMessageError() {
      if (this.messageError) this.messageError.style.display = 'none';
      if (this.message) this.message.classList.remove(this.errClass);
      const inputGroup = this.message?.closest('.input, .textarea');
      if (inputGroup) inputGroup.classList.remove('input--error');
      this._hideErrorBanner();
    },

    _validateTerms() {
      if (!this.terms?.checked) {
        this._setTermsError(this.messages.terms);
        return false;
      }

      this._clearTermsError();
      return true;
    },

    _setTermsError(msg) {
      if (this.termsError) {
        this.termsError.textContent = msg ?? '';
        this.termsError.style.display = '';
      }
      const checkboxWrapper = this.terms?.closest('.checkbox');
      if (checkboxWrapper) {
        checkboxWrapper.classList.add('checkbox--error');
      }
    },

    _clearTermsError() {
      if (this.termsError) this.termsError.style.display = 'none';
      const checkboxWrapper = this.terms?.closest('.checkbox');
      if (checkboxWrapper) {
        checkboxWrapper.classList.remove('checkbox--error');
      }
    },

    /* ---------- error banner ---------- */

    _showErrorBanner() {
      if (!this.errorBanner) {
        this.errorBanner = document.createElement('div');
        this.errorBanner.className = 'contact__error-banner';
        this.errorBanner.setAttribute('role', 'alert');
        this.form.appendChild(this.errorBanner);
      }
      this.errorBanner.textContent = this.messages.banner ?? '';
      this.errorBanner.removeAttribute('hidden');
      this.errorBanner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },

    _hideErrorBanner() {
      // Only hide if all fields are valid
      const hasErrors =
        this.email?.classList.contains(this.errClass) ||
        this.message?.classList.contains(this.errClass) ||
        (this.termsError && this.termsError.textContent.trim());

      if (!hasErrors && this.errorBanner) {
        this.errorBanner.setAttribute('hidden', '');
      }
    },

    /* ---------- submit ---------- */

    _onSubmit(e) {
      const valid =
        this._validateEmail() &&
        this._validateMessage() &&
        this._validateTerms();

      if (!valid) {
        e.preventDefault();
        e.stopPropagation();
        this._showErrorBanner();
        return false;
      }
      // valid === true: do not prevent, form will submit
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ContactForm.init());
  } else {
    ContactForm.init();
  }

})();
